import { useMemo } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney } from "../engine/dates";
import { forecastDrift, outturn, reliability } from "../engine/accuracy";

/**
 * Whether this forecast can be relied on.
 *
 * Everything else here looks forwards. This looks at the forecast's own track
 * record: what it said a week would do, what the week did, and how much the
 * number moved while the week was still ahead.
 *
 * It is built from snapshots taken as the forecast was believed at the time, so
 * it is empty until a week has gone by. Saying so is better than filling the
 * space with a figure worked out from today's data, which would measure nothing.
 */
export default function AccuracyView({ snapshots = [], weeks = [] }) {
  const rows = useMemo(() => outturn(snapshots, weeks), [snapshots, weeks]);
  const drift = useMemo(() => forecastDrift(snapshots, weeks), [snapshots, weeks]);
  const rel = useMemo(() => reliability(rows), [rows]);

  const takenOn = useMemo(
    () => [...new Set(snapshots.map((s) => s.taken_on))].sort(),
    [snapshots]
  );

  const cell = { padding: "8px 10px", fontSize: 12 };
  const th = { ...cell, textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 };
  const money = { ...cell, textAlign: "right", fontFamily: fontMono };
  const card = {
    background: T.panel,
    borderRadius: 10,
    padding: "14px 16px",
    flex: "1 1 200px",
    minWidth: 0,
  };
  const label = { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, color: T.muted };
  const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
  const signed = (n) => `${n > 0 ? "+" : ""}${fmtMoney(n)}`;

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Accuracy</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 780 }}>
        Whether the forecast can be relied on — what it said a week would do against what the week
        did, and how much the number moves while a week is still ahead of us. A snapshot is kept
        each day the tool is opened, so this builds up as it is used rather than being worked out
        after the fact.
      </p>

      {rel && (
        <div style={{ display: "flex", gap: 12, margin: "16px 0", flexWrap: "wrap", maxWidth: 940 }}>
          <div style={card}>
            <div style={label}>Money in · typical miss</div>
            <div style={{ fontFamily: fontDisplay, fontSize: 26 }}>{pct(rel.inError)}</div>
            <div style={{ fontSize: 11, color: T.muted }}>
              {rel.inBias > 0 ? "more arrived than forecast" : "less arrived than forecast"} on
              average
            </div>
          </div>
          <div style={card}>
            <div style={label}>Money out · typical miss</div>
            <div style={{ fontFamily: fontDisplay, fontSize: 26 }}>{pct(rel.outError)}</div>
            <div style={{ fontSize: 11, color: T.muted }}>
              {rel.outBias < 0 ? "more went out than forecast" : "less went out than forecast"} on
              average
            </div>
          </div>
          <div style={card}>
            <div style={label}>Weeks scored</div>
            <div style={{ fontFamily: fontDisplay, fontSize: 26 }}>{rel.weeks}</div>
            <div style={{ fontSize: 11, color: T.muted }}>
              from {takenOn.length} day{takenOn.length === 1 ? "" : "s"} of snapshots
            </div>
          </div>
        </div>
      )}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "22px 0 6px" }}>
        What we said, against what happened
      </h3>
      {rows.length === 0 ? (
        <div
          style={{
            background: T.panel,
            borderRadius: 10,
            padding: 14,
            fontSize: 12,
            color: T.muted,
            lineHeight: 1.6,
            maxWidth: 940,
          }}
        >
          Nothing to score yet — the first snapshot was taken {takenOn.length ? fmtDay(takenOn[0]) : "today"}, and a
          week has to pass before there is a forecast to hold up against an outcome. This fills in
          from next week.
        </div>
      ) : (
        <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", maxWidth: 940 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Week</th>
                <th style={th}>Called</th>
                <th style={{ ...th, textAlign: "right" }}>In · said</th>
                <th style={{ ...th, textAlign: "right" }}>In · did</th>
                <th style={{ ...th, textAlign: "right" }}>Out · said</th>
                <th style={{ ...th, textAlign: "right" }}>Out · did</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.week} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                  <td style={cell}>{fmtDay(r.week)}</td>
                  <td style={{ ...cell, color: T.muted, fontSize: 11 }}>
                    {r.leadDays} day{r.leadDays === 1 ? "" : "s"} ahead
                  </td>
                  <td style={{ ...money, color: T.muted }}>{fmtMoney(r.forecastIn)}</td>
                  <td style={{ ...money, color: r.inError < 0 ? T.cost : T.income }}>
                    {fmtMoney(r.actualIn)}
                    <div style={{ fontSize: 10 }}>{signed(r.inError)}</div>
                  </td>
                  <td style={{ ...money, color: T.muted }}>{fmtMoney(r.forecastOut)}</td>
                  <td style={{ ...money, color: r.outError < 0 ? T.cost : T.ink }}>
                    {fmtMoney(r.actualOut)}
                    <div style={{ fontSize: 10 }}>{signed(r.outError)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "22px 0 6px" }}>
        How much the forecast is moving
      </h3>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 780, marginTop: 0 }}>
        For weeks still ahead: what we first said about them, against what we say now. A week that
        keeps sliding is one to treat carefully when deciding anything.
      </p>
      {drift.length === 0 ? (
        <div
          style={{
            background: T.panel,
            borderRadius: 10,
            padding: 14,
            fontSize: 12,
            color: T.muted,
            lineHeight: 1.6,
            maxWidth: 940,
          }}
        >
          Only one day of snapshots so far, so there is nothing to compare against. Movement shows
          from tomorrow.
        </div>
      ) : (
        <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", maxWidth: 940 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Week</th>
                <th style={th}>Watched since</th>
                <th style={{ ...th, textAlign: "right" }}>In · then / now</th>
                <th style={{ ...th, textAlign: "right" }}>Out · then / now</th>
                <th style={{ ...th, textAlign: "right" }}>Closing balance moved</th>
              </tr>
            </thead>
            <tbody>
              {drift.map((d) => (
                <tr key={d.week} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                  <td style={cell}>{fmtDay(d.week)}</td>
                  <td style={{ ...cell, color: T.muted, fontSize: 11 }}>
                    {fmtDay(d.firstSeen)} · {d.readings} readings
                  </td>
                  <td style={money}>
                    <span style={{ color: T.muted }}>{fmtMoney(d.firstIn)}</span>
                    {" → "}
                    {fmtMoney(d.latestIn)}
                    {Math.abs(d.inMoved) > 1 && (
                      <div style={{ fontSize: 10, color: d.inMoved < 0 ? T.cost : T.income }}>
                        {signed(d.inMoved)}
                      </div>
                    )}
                  </td>
                  <td style={money}>
                    <span style={{ color: T.muted }}>{fmtMoney(d.firstOut)}</span>
                    {" → "}
                    {fmtMoney(d.latestOut)}
                    {Math.abs(d.outMoved) > 1 && (
                      <div style={{ fontSize: 10, color: d.outMoved < 0 ? T.cost : T.ink }}>
                        {signed(d.outMoved)}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      ...money,
                      fontWeight: 600,
                      color: d.closingMoved < 0 ? T.cost : d.closingMoved > 0 ? T.income : T.muted,
                    }}
                  >
                    {Math.abs(d.closingMoved) > 1 ? signed(d.closingMoved) : "held"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 11, color: T.muted, marginTop: 10, lineHeight: 1.6, maxWidth: 780 }}>
        Snapshots cover the next eight weeks and are taken once a day, on the days the tool is
        opened. Nothing is back-filled, so this only ever reflects what was genuinely believed at
        the time.
      </div>
    </div>
  );
}
